import {
  config,
  currentLocale,
  minimizeDigital,
  startDigitalChat,
  digitalChatMode,
  fullPage,
} from '../../store/widget-store';
import { useI18n } from '../../hooks/use-i18n';
import { pickCsMessage } from '../../i18n/widget-content';
import { getAudioPlayer } from '../../services/audio-player';
import type { FrameDragHandleProps } from '../../hooks/use-frame-drag';
import { Typewriter } from './Typewriter';
import { QuickQuestionCarousel } from './QuickQuestionCarousel';
import { sendDigitalAiText } from './chat-actions';

/**
 * 欢迎屏内容 —— 复刻设计稿 WelcomeScreen 右半区(桌面)/ 悬浮卡(移动)。
 * 文案全部走 config(name / welcomeMsg / guideMsg / quickQuestions) + i18n,不硬编码。
 * - 点引导问题:把按钮文案当作问题直接发给 AI(sendDigitalAiText)并进入聊天,与聊天屏内点引导问题一致;
 * - 「立即咨询」CTA:仅进入聊天(startDigitalChat),不自动发消息。
 */
export function WelcomePane({ dragHandleProps }: { dragHandleProps?: FrameDragHandleProps }) {
  const cfg = config.value;
  const { t } = useI18n();
  if (!cfg) return null;

  const csName = pickCsMessage(cfg, 'name', currentLocale.value);
  const intro =
    pickCsMessage(cfg, 'welcomeMsg', currentLocale.value) || t('panel.welcomeFallback');
  const quickHint =
    pickCsMessage(cfg, 'guideMsg', currentLocale.value) || t('panel.welcomeQuickHint');

  return (
    <div class="dcs-welcome-pane">
      {/* header 兼作拖动把手:空白区可拖,关闭按钮经 hitInteractive 自动排除 */}
      <header class="dcs-welcome-head" {...(dragHandleProps ?? {})}>
        <span class="dcs-welcome-head-title">{csName}</span>
        {/* 整页 H5 模式隐藏关闭键(整页即客服,退出交给小程序原生导航) */}
        {!fullPage.value && (
          <button
            type="button"
            class="dcs-icon-btn dcs-welcome-close"
            aria-label={t('panel.closeAria')}
            onClick={minimizeDigital}
          >
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </header>

      <div class="dcs-welcome-body">
        <div class="dcs-welcome-card">
          <div class="dcs-welcome-emoji" aria-hidden="true">👋</div>
          <h3 class="dcs-welcome-title">{csName}</h3>
          <div class="dcs-welcome-intro">
            <Typewriter text={intro} />
          </div>

          {cfg.quickQuestions.length > 0 && (
            <div class="dcs-welcome-quick">
              <span class="dcs-welcome-quick-hint">{quickHint}</span>
              <QuickQuestionCarousel
                questions={cfg.quickQuestions}
                onPick={(text) => {
                  // 启用语音时,在用户点击手势内解锁 autoplay(否则首条 AI 语音会被浏览器拦截)
                  if (cfg.audioEnabled === true) getAudioPlayer().unlock();
                  // 引导问题固定走 AI:防止上次转过人工(mode 残留 'human')导致刚发的 AI 频道消息被过滤掉
                  digitalChatMode.value = 'ai';
                  sendDigitalAiText(text);
                  startDigitalChat();
                }}
              />
            </div>
          )}
        </div>
      </div>

      <footer class="dcs-welcome-foot">
        <button type="button" class="dcs-welcome-cta" onClick={startDigitalChat}>
          {t('panel.startChat')}
        </button>
      </footer>
    </div>
  );
}
